import { selectors } from 'darwin_core';
import { getPendingData } from 'darwin_core/reducers/pending-reducer.js';

import { formatHolidayEnd } from './holidayModel.js';

// Holiday is PRESENTED as a whole-home setting — one Start / one Cancel on the
// Holiday screen, one "Holiday Mode" row on the Property screen — but on the device
// it is per thermostat: each heating (CH) zone carries its own holidayMode and
// holidayModeEnd, and in a multi-zone home one PRT can be away while another is
// not (set from the thermostat itself, or left that way by a partially-applied
// command). darwin_core's isInHolidayMode is already per zone; its
// getNearestHolidayEndTime collapses the home to the earliest end. These helpers
// keep both views honest: a zone-scoped screen reads ITS zone's state and end, a
// home-scoped screen reads WHICH zones are away, and the copy names them.

const NOT_AWAY = Object.freeze({ inHoliday: false, end: undefined });

// One zone's holiday state as { inHoliday, end }. `end` follows the precedence
// getNearestHolidayEndTime applies across the home — this client's optimistic
// pending → a peer's accepted into-holiday goal (shared desired state) → the
// reported value — but for this zone alone, so two thermostats away until
// different dates each read their own. A hot-water zone resolves through the CH
// zone on the same thermostat (holiday is set on the heating endpoint; setHoliday
// seeds the DHW pending too, so both are checked). `end` can be blank when the
// device reported holiday with no end; formatHolidayEnd yields '' for that.
export function zoneHolidayStatus(state, homeId, zoneId) {
  // isInHolidayMode returns the number 2 — a "no heating zone on this thermostat"
  // sentinel, not a holiday — for a zone with no CH sibling (a malformed frame).
  // useZoneControls coerces that truthy and locks the controls, which is the safe
  // failure; but the notice must not then claim a holiday darwin_core cannot
  // confirm, so only an explicit true counts here.
  if (!zoneId || selectors.isInHolidayMode(state, homeId, zoneId) !== true) {
    return NOT_AWAY;
  }
  const chZone = selectors.getChZoneForDhwZone(state, homeId, zoneId);
  const chZoneId = chZone?.zoneId ?? zoneId;

  // pendingHolidayMode holds both directions: entering writes holidayMode 3 with
  // the end, a cancel writes holidayMode 0 and no end. Only an into-holiday pending
  // carries an end, so filter as getNearestHolidayEndTime does (holidayMode === 3).
  const pending =
    intoHolidayPending(
      getPendingData(state.pendingHolidayMode, homeId, zoneId),
    ) ??
    intoHolidayPending(
      getPendingData(state.pendingHolidayMode, homeId, chZoneId),
    );
  if (pending) {
    // setHoliday / startHolidayAllZones write holidayEndTime AND holidayModeEnd
    // together; read the reported-shaped key, as getNearestHolidayEndTime does.
    return { inHoliday: true, end: pending.holidayModeEnd };
  }
  const shared = selectors.getSharedHolidayPending(state, homeId, chZoneId);
  if (shared) {
    return { inHoliday: true, end: shared.holidayEndTime };
  }
  return { inHoliday: true, end: chZone?.holidayModeEnd };
}

const intoHolidayPending = (pending) =>
  pending && pending.holidayMode === 3 ? pending : undefined;

const NO_ZONES = Object.freeze({
  total: 0,
  away: [],
  all: false,
  nearestEnd: undefined,
});

// The home's holiday picture for a home-scoped screen: which heating zones are
// away (each with its name and end), whether that is ALL of them, and the earliest
// end among them — what the whole-home wording shows. Hot-water zones are not
// counted: their holiday is their CH sibling's. A zone that arrived with no
// [prtId, endpointId] tuple is left out of the count (darwin_core's own lookups
// are not guarded against it; the ErrorBoundary is the backstop for that, as for
// every other reader of the zone list).
export function homeHolidayStatus(state, homeId) {
  const chZones = selectors
    .getChZones(state, homeId)
    .filter((zone) => Array.isArray(zone.zoneId));
  if (chZones.length === 0) {
    return NO_ZONES;
  }

  const away = [];
  for (const zone of chZones) {
    const { inHoliday, end } = zoneHolidayStatus(state, homeId, zone.zoneId);
    if (inHoliday) {
      away.push({
        zoneId: zone.zoneId,
        name: selectors.getPrtFriendlyName(state, homeId, zone.zoneId),
        end,
      });
    }
  }

  return {
    total: chZones.length,
    away,
    all: away.length === chZones.length,
    nearestEnd: earliest(away.map((zone) => zone.end)),
  };
}

// The earliest of the given ends BY TIME. Deliberately not darwin_core's
// getNearestHolidayEndTime, whose bare .sort() compares the raw values as strings:
// an optimistic pending end is a Date while a reported one is an ISO string, so in
// the window after Start it can order them wrongly and show the other zone's date.
// Blank or unparseable ends are ignored (a device can report holiday with no
// end); undefined when none is usable.
function earliest(ends) {
  let best;
  for (const end of ends) {
    if (!end) {
      continue;
    }
    const time = new Date(end).getTime();
    if (Number.isNaN(time)) {
      continue;
    }
    if (best === undefined || time < best.time) {
      best = { end, time };
    }
  }
  return best?.end;
}

// Equality for useSelector: homeHolidayStatus rebuilds its result on every call, so
// compare by content (which zones, their names and ends) to keep a screen from
// re-rendering on every unrelated store change.
export function sameHomeHolidayStatus(a, b) {
  if (a === b) {
    return true;
  }
  if (
    a.total !== b.total ||
    a.all !== b.all ||
    a.away.length !== b.away.length ||
    String(a.nearestEnd ?? '') !== String(b.nearestEnd ?? '')
  ) {
    return false;
  }
  return a.away.every((zone, i) => {
    const other = b.away[i];
    return (
      zone.zoneId.join('_') === other.zoneId.join('_') &&
      zone.name === other.name &&
      String(zone.end ?? '') === String(other.end ?? '')
    );
  });
}

// The zone-scoped notice's sentence: this thermostat, its own end.
export function zoneHolidayMessage(name, end) {
  const until = formatHolidayEnd(end);
  return `${name || 'This thermostat'} is in Holiday Mode${
    until ? ` until ${until}` : ''
  }.`;
}

// The home-scoped notice's sentence. Every heating zone away: the app's whole-home
// wording. Only some: name the ones that are, so a user whose other thermostat is
// still controllable is not told their whole home is away. Either way the end shown
// is the earliest, as the Holiday screen and the Property row show.
export function homeHolidayMessage({ away, all, nearestEnd }) {
  const until = formatHolidayEnd(nearestEnd);
  const untilClause = until ? ` until ${until}` : '';
  if (all) {
    return `Your heating is in Holiday Mode${untilClause}.`;
  }
  const names = away.map((zone) => zone.name || 'A thermostat');
  const verb = names.length === 1 ? 'is' : 'are';
  return (
    `${listNames(names)} ${verb} in Holiday Mode${untilClause}. ` +
    'Other zones are not affected.'
  );
}

// "A" / "A and B" / "A, B and C".
function listNames(names) {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
